import {produce} from 'immer';

import {assert} from 'Asserts';
import {MinimalStorableSummaryWithSchemaId} from 'constants/SampleConstants';
import {BioentitySummary, TargetStorable} from 'folder-items/FileConstants';
import {
  StructuredTableKind,
  StructuredTableRow,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {toStorableTypedValueCell, toBioentityTypedValueCell} from 'notebook/tables/typed-values/DataTypes';
import {StorableLinkSchemaFieldTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import ProcessTaskStructuredTableAdapter, {
  ProcessTaskStructuredTable,
  ProcessTaskStructuredTableRowMetadata,
} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import APIUtil from 'util/APIUtil';

function makeNewRowWithCell({
  table,
  cell,
  rowNum,
  colNum,
  container,
}: {
  table: ProcessTaskStructuredTable;
  cell: Cell | null;
  rowNum: number;
  colNum: number;
  container: TargetStorable | null;
}): StructuredTableRow<ProcessTaskStructuredTableRowMetadata> | null {
  assert(colNum >= 0, 'Unexpected column index when creating new row', {colNum, table});
  const adapter = ProcessTaskStructuredTableAdapter.getAdapter();
  const blankRow = adapter.getBlankRow(table.metadata, rowNum, table.columns);
  if (container != null) {
    const containerTypedValue: StorableLinkSchemaFieldTypedValue = {
      type: FieldType.STORABLE_LINK,
      value: '',
      targetStorables: [
        {
          id: container.id,
          name: container.name,
          iconId: container.iconId,
        },
      ],
    };
    return produce(blankRow, (draftBlankRow) => {
      draftBlankRow.cells[colNum].typedValue = containerTypedValue;
      return draftBlankRow;
    });
  } else if (cell != null) {
    return produce(blankRow, (draftBlankRow) => {
      draftBlankRow.cells[colNum] = cell;
      return draftBlankRow;
    });
  }
  return null;
}

export type StorableSummaryWithContainer = MinimalStorableSummaryWithSchemaId & {
  container?: TargetStorable | null;
};

export function propagateItemsToDestinationTable({
  items,
  destinationColumnIndex,
  destinationTable,
}: {
  items: Array<BioentitySummary> | Array<StorableSummaryWithContainer>;
  destinationColumnIndex: number;
  destinationTable: ProcessTaskStructuredTable;
}): ProcessTaskStructuredTable {
  const isStorable = (
    item: BioentitySummary | MinimalStorableSummaryWithSchemaId
  ): item is MinimalStorableSummaryWithSchemaId => APIUtil.isStorable(item.id);

  const updatedTable = produce(destinationTable, (draftTable) => {
    const newRows: Array<StructuredTableRow<ProcessTaskStructuredTableRowMetadata>> = [];
    items.forEach((item, idx) => {
      const cell = isStorable(item) ? toStorableTypedValueCell([item]) : toBioentityTypedValueCell(item);
      const newRow = makeNewRowWithCell({
        table: destinationTable,
        cell,
        rowNum: idx,
        colNum: destinationColumnIndex,
        container: isStorable(item) ? (item.container ?? null) : null,
      });
      if (newRow != null) {
        newRows.push(newRow);
      }
    });
    if (newRows.length > 0) {
      draftTable.rows = newRows;
    }
  });
  return updatedTable;
}

export interface ItemColumnMapping {
  /**
   * Items to write into `destinationColumnIndex`, one per destination row by index. Sparse entries
   * (`null` / `undefined`) leave that row's cell at the schema default — used when one mapping in a
   * multi-mapping submission has fewer values than another.
   */
  items: Array<BioentitySummary | null | undefined> | Array<StorableSummaryWithContainer | null | undefined>;
  destinationColumnIndex: number;
}

/**
 * Populate multiple destination columns in a single empty task table — one row in the destination
 * table per source row, with each row carrying the values from every mapped column.
 *
 * Used by the "Create workflow tasks" shortcut when the user maps several source columns at once
 * (the multi-row Column mappings table). Calling `propagateItemsToDestinationTable` once per
 * mapping wouldn't work because each call replaces the entire `rows` array; this function instead
 * builds each destination row by composing all mappings for the same row index.
 *
 * Behavior notes:
 *   - Empty `columnMappings` is a no-op (returns the destination table unchanged).
 *   - A single mapping delegates to `propagateItemsToDestinationTable` for parity with that path.
 *   - For multiple mappings, the number of created rows is the longest `items` list across
 *     mappings; rows past a mapping's end leave that mapping's destination cell as the schema
 *     default (i.e. blank), so columns of unequal length are zipped without truncating data.
 *   - Each mapping's items are placed into its `destinationColumnIndex`. A storable's
 *     `container`, if present, is preserved as a destination-well cell value.
 */
export function propagateMultipleItemColumnsToDestinationTable({
  columnMappings,
  destinationTable,
}: {
  columnMappings: Array<ItemColumnMapping>;
  destinationTable: ProcessTaskStructuredTable;
}): ProcessTaskStructuredTable {
  if (columnMappings.length === 0) {
    return destinationTable;
  }
  if (columnMappings.length === 1) {
    // Single-mapping fast path; identical to the legacy single-column propagation.
    // `propagateItemsToDestinationTable` expects a dense items list, so drop any sparse entries
    // — sparse arrays only occur for multi-mapping inputs in practice.
    const {items, destinationColumnIndex} = columnMappings[0];
    return propagateItemsToDestinationTable({
      items: items.filter((item) => item != null) as
        | Array<BioentitySummary>
        | Array<StorableSummaryWithContainer>,
      destinationColumnIndex,
      destinationTable,
    });
  }

  // Distinguish bioentity items from storable items by API id prefix; storables can also carry a
  // `container` (used for destination-well cells in transfer-style tables).
  const isStorable = (
    item: BioentitySummary | MinimalStorableSummaryWithSchemaId
  ): item is MinimalStorableSummaryWithSchemaId => APIUtil.isStorable(item.id);

  // We emit as many rows as the longest mapping; for shorter mappings we simply leave their
  // destination cell at the schema default (blank), so columns of unequal length are zipped.
  const rowCount = Math.max(...columnMappings.map((m) => m.items.length));
  const adapter = ProcessTaskStructuredTableAdapter.getAdapter();
  return produce(destinationTable, (draftTable) => {
    const newRows: Array<StructuredTableRow<ProcessTaskStructuredTableRowMetadata>> = [];
    for (let rowNum = 0; rowNum < rowCount; rowNum++) {
      // Start each destination row from a blank task row so non-mapped columns get their
      // schema-driven defaults.
      const draftRow = adapter.getBlankRow(destinationTable.metadata, rowNum, destinationTable.columns);
      for (const {items, destinationColumnIndex} of columnMappings) {
        const item = items[rowNum];
        if (item == null) {
          // This mapping has no item at this row index; leave the destination cell blank.
          continue;
        }
        const cell = isStorable(item) ? toStorableTypedValueCell([item]) : toBioentityTypedValueCell(item);
        const newRow = makeNewRowWithCell({
          table: destinationTable,
          cell,
          rowNum,
          colNum: destinationColumnIndex,
          container: isStorable(item) ? (item.container ?? null) : null,
        });
        if (newRow != null) {
          // `getBlankRow` returns a fresh row each iteration, so we can mutate `cells` directly
          // here instead of running the cell merge through another `produce`.
          draftRow.cells[destinationColumnIndex] = newRow.cells[destinationColumnIndex];
        }
      }
      newRows.push(draftRow);
    }
    if (newRows.length > 0) {
      draftTable.rows = newRows;
    }
  });
}

export default function propagateContentFromSourceToDestinationTable({
  sourceColumnIndex,
  sourceTable,
  sourceComputedTable,
  destinationColumnIndex,
  destinationTable,
  containers,
}: {
  sourceColumnIndex: number;
  sourceTable: UnknownStructuredTable;
  sourceComputedTable?: Array<Array<EvaluatedCell>> | null;
  destinationColumnIndex: number;
  destinationTable: ProcessTaskStructuredTable;
  containers: Array<TargetStorable> | null;
}): ProcessTaskStructuredTable {
  return produce(destinationTable, (draftTable) => {
    const newRows: Array<StructuredTableRow<ProcessTaskStructuredTableRowMetadata>> = [];
    for (let rowNum = 0; rowNum < sourceTable.rows.length; rowNum++) {
      const sourceRow = sourceTable.rows[rowNum] ?? {};
      const computedValue = sourceComputedTable?.[rowNum][sourceColumnIndex]?.value;
      const newRow = makeNewRowWithCell({
        table: destinationTable,
        cell: computedValue
          ? {text: '', typedValue: computedValue}
          : (sourceRow.cells?.[sourceColumnIndex] ?? null),
        rowNum,
        colNum: destinationColumnIndex,
        container: containers?.[rowNum] ?? null,
      });
      if (
        newRow != null &&
        sourceRow.lastSyncedArchivePurpose == null &&
        // Lookup tables' rows are never finalized because they are just data lookups - they do not perform
        // actions that need to be submitted to the server.
        (sourceTable.kind === StructuredTableKind.LOOKUP || sourceRow.isFinalized)
      ) {
        newRows.push(newRow);
      }
    }
    if (newRows.length > 0) {
      draftTable.rows = newRows;
    }
  });
}
